import React, { useState } from 'react';
import {
  useGetCompanyByPrefix,
  useGetCompanyInfoById,
} from '@frx/frx-react-context';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { SearchableSelect } from '../SearchableSelect';
import { useDebounce, useSessionStorage } from '../../hooks';
import { DTD_ID } from '../../utils';
import { DoubleTheDonationCompanyInfo } from './DoubleTheDonationCompanyInfo';
import { MoneyDollar, MoneyDollarOutline } from '../../assets';

export type DoubleTheDonationSearchProps = {
  hasCompanyInfo?: boolean;
};

export const DoubleTheDonationSearch = ({
  hasCompanyInfo = false,
}: DoubleTheDonationSearchProps) => {
  const [newEmployer, setNewEmployer] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [, setSelectedValue] = useState('');

  const [defaultDtdValue, setValue] = useSessionStorage(DTD_ID, '');

  const debounceSearchValue = useDebounce(searchValue);
  const { data, isInitialLoading } = useGetCompanyByPrefix({
    companyPrefix: debounceSearchValue,
  });
  const { data: companyData } = useGetCompanyInfoById({
    companyId: defaultDtdValue,
  });

  const handleOnInputChange = (value: string) => {
    setSearchValue(value);
  };

  const handleChange = (selectedOption: any) => {
    setSelectedValue(selectedOption?.value);
    setValue(selectedOption?.value);
    setNewEmployer(false);
  };

  if (!newEmployer && companyData && hasCompanyInfo)
    return (
      <Stack>
        <Button
          colorScheme="blue"
          variant="link"
          onClick={() => setNewEmployer(true)}
        >
          Find new employer
        </Button>
        <DoubleTheDonationCompanyInfo companyData={companyData} />
      </Stack>
    );

  return (
    <Stack
      alignItems="center"
      bg="alerts.info.background"
      border="0.5px solid"
      borderColor="alerts.info.border"
      borderRadius="md"
      box-shadow=" 0px 3px 6px 0px rgba(0, 0, 0, 0.08)"
      justifyContent="center"
      p={8}
      spacing={6}
      w="full"
    >
      <HStack spacing={6} maxW="325px">
        <Flex
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            as={MoneyDollar}
            boxSize={12}
            color="brand.secondary.swatch10"
            zIndex={1}
          />
          <Flex
            position="absolute"
            top="-18px"
            right="-12px"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={MoneyDollarOutline} boxSize={8} color="currentColor" />
          </Flex>
        </Flex>
        <VStack align="stretch">
          <Text as="b" fontSize="24px">
            Don’t miss out!
          </Text>
          <Text fontSize="md" fontWeight={400}>
            Your employer may <b>match</b> your donation to St. Jude.
          </Text>
        </VStack>
      </HStack>
      <Box w="full" maxW="325px" bg="white">
        <SearchableSelect
          isLoading={isInitialLoading}
          menuIsOpen={!!searchValue}
          name="company_name"
          onInputChange={handleOnInputChange}
          onChange={handleChange}
          options={data}
          placeholder="Find your employer"
          showNoDropDown
        />
      </Box>
    </Stack>
  );
};
